import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { debounce } from 'lodash-es'

import { PrimaryButton } from 'Components/_theme/PrimaryButton/PrimaryButton'
import { SelectAsync } from 'Components/SelectAsync/SelectAsync'

import {
  buildDeviceOptionLabel,
  PortalReturnLabelDeviceOption,
  type PortalReturnLabelDeviceOptionData,
} from './PortalReturnLabelDeviceOption'
import { PortalReturnLabelDeviceSingleValue } from './PortalReturnLabelDeviceSingleValue'
import { PORTAL_RETURN_LABEL_MAX_DEVICES } from './PortalReturnLabelModal.constants'
import type { PortalReturnLabelModalAddDeviceProps } from './PortalReturnLabelModal.types'
import { PortalReturnLabelModalGhostXButton } from './PortalReturnLabelModalGhostXButton'

const ADD_DEVICE_SELECT_INPUT_ID = 'PortalReturnLabelModal-addDeviceSelect'

type LoadOptionsCallback = (
  options: PortalReturnLabelDeviceOptionData[],
) => void

export const PortalReturnLabelModalAddDevice = ({
  excludedReturnCaseIds,
  isAdmin,
  isAtLimit,
  onAdd,
  search,
}: PortalReturnLabelModalAddDeviceProps) => {
  const intl = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const [picked, setPicked] =
    useState<PortalReturnLabelDeviceOptionData | null>(null)
  const activeRequestId = useRef(0)

  // SelectAsync forwards ref through a Controller, so a ref-based focus call
  // would hit the wrapper rather than the input. Query the rendered input by
  // id instead.
  useEffect(() => {
    if (!isOpen) {
      return
    }

    const input = document.querySelector(`#${ADD_DEVICE_SELECT_INPUT_ID}`)

    if (input instanceof HTMLElement) {
      input.focus()
    }
  }, [isOpen])

  const invalidateInFlightLoad = useCallback(() => {
    activeRequestId.current += 1
  }, [])

  const debouncedLoadOptions = useMemo(
    () =>
      debounce((term: string, callback: LoadOptionsCallback) => {
        const excludedSet = new Set(excludedReturnCaseIds.map(String))

        activeRequestId.current += 1

        const requestId = activeRequestId.current

        search(term)
          .then(results => {
            if (requestId !== activeRequestId.current) {
              return
            }

            callback(
              results
                .filter(rc => !excludedSet.has(String(rc.id)))
                .map(rc => ({
                  label: buildDeviceOptionLabel(rc),
                  returnCase: rc,
                  value: String(rc.id),
                })),
            )
          })
          .catch(() => {
            if (requestId !== activeRequestId.current) {
              return
            }

            callback([])
          })
      }, 300),
    [excludedReturnCaseIds, search],
  )

  useEffect(
    () => () => {
      debouncedLoadOptions.cancel()
      invalidateInFlightLoad()
    },
    [debouncedLoadOptions, invalidateInFlightLoad],
  )

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    setPicked(null)
  }, [])

  const handleCancel = useCallback(() => {
    debouncedLoadOptions.cancel()
    invalidateInFlightLoad()
    setPicked(null)
    setIsOpen(false)
  }, [debouncedLoadOptions, invalidateInFlightLoad])

  const handleAdd = useCallback(() => {
    if (!picked) {
      return
    }

    debouncedLoadOptions.cancel()
    invalidateInFlightLoad()
    onAdd(picked.returnCase)
    setPicked(null)
    setIsOpen(false)
  }, [debouncedLoadOptions, invalidateInFlightLoad, onAdd, picked])

  if (!isOpen) {
    if (isAtLimit) {
      return (
        <div
          aria-live="polite"
          className="PortalReturnLabelModal-addDeviceTriggerWrap d-flex justify-content-center align-items-center mb-6"
          role="status"
        >
          <p className="text-muted small m-0">
            {intl.formatMessage(
              { id: 'PortalReturnLabelModal_max_devices_reached' },
              { amount: PORTAL_RETURN_LABEL_MAX_DEVICES },
            )}
          </p>
        </div>
      )
    }

    return (
      <div className="PortalReturnLabelModal-addDeviceTriggerWrap d-flex justify-content-center align-items-center mb-6">
        <PrimaryButton
          appearance="outline"
          iconLeft={faPlus}
          onClick={handleOpen}
        >
          {intl.formatMessage({
            id: 'PortalReturnLabelModal_add_device_trigger',
          })}
        </PrimaryButton>
      </div>
    )
  }

  return (
    <div className="PortalReturnLabelModal-addDeviceRow d-flex gap-3 align-items-center mb-6">
      <div className="flex-grow-1">
        <SelectAsync<PortalReturnLabelDeviceOptionData>
          aria-label={intl.formatMessage({
            id: 'PortalReturnLabelModal_add_device_aria_label',
          })}
          components={{
            Option: PortalReturnLabelDeviceOption,
            SingleValue: PortalReturnLabelDeviceSingleValue,
          }}
          defaultOptions={false}
          inputId={ADD_DEVICE_SELECT_INPUT_ID}
          isClearable
          loadOptions={debouncedLoadOptions}
          noOptionsMessage={({ inputValue }) =>
            inputValue
              ? intl.formatMessage({
                  id: isAdmin
                    ? 'PortalReturnLabelModal_no_eligible_cases_admin'
                    : 'PortalReturnLabelModal_no_eligible_cases_employee',
                })
              : intl.formatMessage({ id: 'No_devices_found' })
          }
          onChange={setPicked}
          placeholder={intl.formatMessage({
            id: 'PortalReturnLabelModal_add_device_placeholder',
          })}
          value={picked}
        />
      </div>
      <PrimaryButton disabled={!picked} onClick={handleAdd}>
        {intl.formatMessage({
          id: 'PortalReturnLabelModal_add_device_confirm',
        })}
      </PrimaryButton>
      <PortalReturnLabelModalGhostXButton
        ariaLabel={intl.formatMessage({ id: 'Cancel' })}
        onClick={handleCancel}
      />
    </div>
  )
}
